import React, { useEffect, useState } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  TextInput,
  Modal,
} from 'react-native';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { primaryPhysicianInfoService } from '../../../../services/primaryPhysicianInfo';
import { Alert, ActivityIndicator } from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { vaultService } from '../../../../services/vaultService';

import { createLogger } from '../../../../utils/logger';
import { ensureCredentialWriteKey } from '../../../../services/encryption/credentialFields';
const logger = createLogger('PrimaryPhysicianInput');

const US_STATES = [
  'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
  'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
  'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
  'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
  'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
];

export const PrimaryPhysicianInputScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, { primaryPhysicianData?: any; isEditing?: boolean }>, string>>();
  const [showStateModal, setShowStateModal] = useState(false);
  const [showUsername, setShowUsername] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [hasOnlinePortal, setHasOnlinePortal] = useState(false);
  const { primaryPhysicianData, isEditing } = route.params || {};
  const isEdit = !!isEditing;

  // Helper: format phone as (xxx) xxx-xxxx while typing
  const formatPhoneNumber = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 10) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
    } else if (cleaned.length >= 6) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
    } else if (cleaned.length >= 3) {
      return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3)}`;
    }
    return cleaned;
  };

  // Helper: format date as MM/DD/YYYY while typing
  const formatDate = (text: string) => {
    const cleaned = (text || '').replace(/\D/g, '');
    if (cleaned.length >= 8) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4, 8)}`;
    } else if (cleaned.length >= 4) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2, 4)}/${cleaned.slice(4)}`;
    } else if (cleaned.length >= 2) {
      return `${cleaned.slice(0, 2)}/${cleaned.slice(2)}`;
    }
    return cleaned;
  };

  // Helper: parse concatenated address back into components
  // Address format: "123 Main St Dallas, TX 75201"
  const parseAddress = (fullAddress: string) => {
    if (!fullAddress) {
      return { street: '', city: '', state: '', zip: '' };
    }

    let street = '';
    let city = '';
    let state = '';
    let zip = '';

    // Try to extract zip (5 digits at the end)
    const zipMatch = fullAddress.match(/\b(\d{5})\s*$/);
    if (zipMatch) {
      zip = zipMatch[1];
      const remaining = fullAddress.replace(/\b\d{5}\s*$/, '').trim();

      // Try to extract state (2 letters before zip, potentially after comma)
      const stateMatch = remaining.match(/,?\s*([A-Z]{2})\s*$/);
      if (stateMatch) {
        state = stateMatch[1];
        const streetAndCity = remaining.replace(/,?\s*[A-Z]{2}\s*$/, '').trim();

        // Find the last comma to separate city from street
        const lastCommaIndex = streetAndCity.lastIndexOf(',');
        if (lastCommaIndex > -1) {
          street = streetAndCity.substring(0, lastCommaIndex).trim();
          city = streetAndCity.substring(lastCommaIndex + 1).trim();
        } else {
          // No comma found, try to split by looking for common street patterns
          // This is a fallback - try to find where street ends and city begins
          const parts = streetAndCity.split(' ');
          if (parts.length > 1) {
            // Assume the last 1-2 words are the city
            if (parts.length <= 3) {
              street = parts.slice(0, -1).join(' ');
              city = parts[parts.length - 1];
            } else {
              street = parts.slice(0, -2).join(' ');
              city = parts.slice(-2).join(' ');
            }
          } else {
            street = streetAndCity;
          }
        }
      } else {
        // No state found, everything before zip is street + city
        street = remaining;
      }
    } else {
      // No zip found, try to parse what we can
      const commaIndex = fullAddress.lastIndexOf(',');
      if (commaIndex > -1) {
        street = fullAddress.substring(0, commaIndex).trim();
        const afterComma = fullAddress.substring(commaIndex + 1).trim();

        // Check if after comma looks like "STATE" or "CITY STATE"
        const stateMatch = afterComma.match(/^([A-Z]{2})$/);
        if (stateMatch) {
          state = stateMatch[1];
        } else {
          city = afterComma;
        }
      } else {
        street = fullAddress;
      }
    }

    return { street, city, state, zip };
  };

  const [formData, setFormData] = useState({
    physicianName: '',
    practiceName: '',
    officePhone: '',
    faxNumber: '',
    email: '',
    username: '',
    password: '',
    street: '',
    city: '',
    state: '',
    zip: '',
    yearsAsPatient: '',
    lastVisitDate: '',
    nextAppointmentDate: '',
    medicalRecordNumber: '',
    insuranceAccepted: '',
    referralRequired: false,
    emergencyContact: false,
    portalUrl: '',
    specialInstructions: '',
  });
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isEdit && primaryPhysicianData) {
      // Parse the address back into components
      const addressParts = parseAddress(primaryPhysicianData.address || '');
      logger.debug('[Address Debug] Original address:', primaryPhysicianData.address);
      logger.debug('[Address Debug] Parsed address parts:', addressParts);

      setFormData(prev => ({
        ...prev,
        physicianName: primaryPhysicianData.physicianName || '',
        practiceName: primaryPhysicianData.practiceName || '',
        officePhone: formatPhoneNumber(primaryPhysicianData.phoneNumber || ''),
        faxNumber: formatPhoneNumber(primaryPhysicianData.faxNumber || ''),
        email: primaryPhysicianData.email || '',
        street: addressParts.street,
        city: addressParts.city,
        state: addressParts.state,
        zip: addressParts.zip,
        yearsAsPatient: primaryPhysicianData.yearsAsPatient?.toString() || '',
        lastVisitDate: primaryPhysicianData.lastVisitDate || '',
        nextAppointmentDate: primaryPhysicianData.nextAppointmentDate || '',
        medicalRecordNumber: primaryPhysicianData.medicalRecordNumber || '',
        insuranceAccepted: primaryPhysicianData.insuranceAccepted || '',
        referralRequired: primaryPhysicianData.referralRequired || false,
        emergencyContact: primaryPhysicianData.emergencyContact || false,
        portalUrl: primaryPhysicianData.portalUrl || '',
        specialInstructions: primaryPhysicianData.notes || '',
      }));

      // Try to load portal credentials if we have an ID
      if (primaryPhysicianData.id) {
        (async () => {
          try {
            const creds = await vaultService.getPortalCredentials(primaryPhysicianData.id, 'primary_physician');
            if (creds && (creds.username || creds.password)) {
              setHasOnlinePortal(true);
              setFormData(prev => ({
                ...prev,
                username: creds.username || '',
                password: creds.password || '',
              }));
            }
          } catch (error) {
            logger.debug('Failed to load portal credentials:', error);
          }
        })();
      }
    }
  }, [isEdit, primaryPhysicianData]);

  const handleSubmit = async () => {
    try {
      setSubmitting(true);
      // K5: a login is saved only under a Recovery Code; ask before anything is written.
      if ((formData.username || formData.password) && !(await ensureCredentialWriteKey())) {
        setSubmitting(false);
        return;
      }
      // Build backend payload
      const fullAddress = [
        formData.street?.trim(),
        [formData.city?.trim(), formData.state?.trim()].filter(Boolean).join(', '),
        formData.zip?.trim(),
      ]
        .filter(Boolean)
        .join(' ');

      const payload: any = {
        physicianName: formData.physicianName || undefined,
        practiceName: formData.practiceName || undefined,
        phoneNumber: formData.officePhone.replace(/\D/g, ''),
        faxNumber: formData.faxNumber.replace(/\D/g, ''),
        email: formData.email || undefined,
        address: fullAddress || undefined,
        city: formData.city || undefined,
        state: formData.state || undefined,
        zipCode: formData.zip || undefined,
        yearsAsPatient: formData.yearsAsPatient ? parseInt(formData.yearsAsPatient, 10) : undefined,
        lastVisitDate: formData.lastVisitDate || undefined,
        nextAppointmentDate: formData.nextAppointmentDate || undefined,
        medicalRecordNumber: formData.medicalRecordNumber || undefined,
        insuranceAccepted: formData.insuranceAccepted || undefined,
        referralRequired: formData.referralRequired,
        emergencyContact: formData.emergencyContact,
        portalUrl: formData.portalUrl || undefined,
        notes: formData.specialInstructions || undefined,
      };

      // Create or update primary physician record
      let savedId = primaryPhysicianData?.id as string | undefined;
      if (isEdit && primaryPhysicianData?.id) {
        const updated = await primaryPhysicianInfoService.updatePrimaryPhysicianInfo(primaryPhysicianData.id, payload);
        // Handle wrapped response format { success, data, message }
        const updatedData = updated?.data || updated;
        savedId = updatedData.id;
      } else {
        const saved = await primaryPhysicianInfoService.createPrimaryPhysicianInfo(payload);
        // Handle wrapped response format { success, data, message }
        const createdData = saved?.data || saved;
        savedId = createdData.id;
      }

      // Store portal credentials in Vault if provided
      if (savedId && (formData.username || formData.password)) {
        try {
          logger.debug('[Portal Debug] Storing portal credentials in vault for primary physician ID:', savedId);
          logger.debug('[Portal Debug] Username provided:', !!formData.username);
          logger.debug('[Portal Debug] Password provided:', !!formData.password);
          await vaultService.storePortalCredentials(savedId, 'primary_physician', {
            username: formData.username,
            password: formData.password,
          });
          logger.debug('[Portal Debug] Successfully stored portal credentials in vault');
        } catch (e) {
          logger.error('[Portal Debug] Vault write failed for primary physician portal credentials:', e);
        }
      } else {
        logger.debug('[Portal Debug] No savedId or no credentials provided, skipping vault storage', { savedId, hasUsername: !!formData.username, hasPassword: !!formData.password });
      }

      // Navigate to lock/unlock with real id
      (navigation as any).navigate('PrimaryPhysicianLockUnlock', { primaryPhysicianId: savedId });
    } catch (error: any) {
      logger.error('Error saving primary physician info:', error);
      Alert.alert('Error', 'Failed to save primary physician information. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  const handleStateSelect = (state: string) => {
    setFormData({ ...formData, state });
    setShowStateModal(false);
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={[styles.backIcon, { tintColor: '#fff' }]}
          />
        </TouchableOpacity>
      </View>

      <View style={styles.topSection}>
        <Image
          source={require('../../../../assets/icons/keyimage.png')}
          style={styles.backgroundKey}
        />
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <Text style={styles.title}>Add Your Primary Physician Info</Text>
      </View>

      <View style={styles.inputContainer}>
        <ScrollView style={styles.scrollView} contentContainerStyle={styles.scrollViewContent} keyboardShouldPersistTaps="handled" automaticallyAdjustKeyboardInsets={true}>
          <View style={styles.inputGroup}>
            <Text style={styles.label}>Physician Name</Text>
            <TextInput
              style={styles.input}
              value={formData.physicianName}
              onChangeText={(text) => setFormData({ ...formData, physicianName: text })}
              placeholder="Enter Physician Name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Practice Name</Text>
            <TextInput
              style={styles.input}
              value={formData.practiceName}
              onChangeText={(text) => setFormData({ ...formData, practiceName: text })}
              placeholder="Enter Practice name"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Office Phone</Text>
            <TextInput
              style={styles.input}
              value={formData.officePhone}
              onChangeText={(text) => setFormData({ ...formData, officePhone: formatPhoneNumber(text) })}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Fax Number</Text>
            <TextInput
              style={styles.input}
              value={formData.faxNumber}
              onChangeText={(text) => setFormData({ ...formData, faxNumber: formatPhoneNumber(text) })}
              placeholder="(xxx) xxx-xxxx"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={14}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Email</Text>
            <TextInput
              style={styles.input}
              value={formData.email}
              onChangeText={(text) => setFormData({ ...formData, email: text.trim().toLowerCase() })}
              placeholder="Enter Email"
              placeholderTextColor="#A9A9A9"
              keyboardType="email-address"
              autoCapitalize="none"
              autoCorrect={false}
              textContentType="emailAddress"
            />
          </View>

          <View style={styles.inputGroup}>
            <View style={styles.toggleContainer}>
              <Text style={styles.toggleLabel}>Does your physician have an online portal?</Text>
              <View style={styles.toggleWrapper}>
                <TouchableOpacity
                  style={[styles.toggleButton, hasOnlinePortal && styles.toggleButtonActive]}
                  onPress={() => setHasOnlinePortal(!hasOnlinePortal)}
                >
                  <View style={[styles.toggleCircle, hasOnlinePortal && styles.toggleCircleActive]} />
                </TouchableOpacity>
                <Text style={styles.toggleText}>{hasOnlinePortal ? 'Yes' : 'No'}</Text>
              </View>
            </View>
          </View>

          {hasOnlinePortal && (
            <>
              <View style={styles.securityNoteContainer}>
                <Image
                  source={require('../../../../assets/icons/info.png')}
                  style={styles.infoIcon}
                  resizeMode="contain"
                />
                <Text style={styles.securityNoteText}>
                  <Text style={styles.securityNoteTitle}>Security Note: </Text>
                  If you unlock this data for your Keyholders or Successors, they will be able to view the portal credentials you enter here. For maximum security, consider storing sensitive portal information in the Passwords section instead, which has stricter access controls. Portal credentials will only be shared with your successor after your End of Life event.
                </Text>
              </View>
              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Website Address</Text>
                <TextInput
                  style={styles.input}
                  value={formData.portalUrl}
                  onChangeText={(text) => setFormData({ ...formData, portalUrl: text })}
                  placeholder="Enter Portal Website URL"
                  placeholderTextColor="#A9A9A9"
                  keyboardType="url"
                  autoCapitalize="none"
                  autoCorrect={false}
                />
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Username 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.username}
                    onChangeText={(text) => setFormData({ ...formData, username: text })}
                    placeholder="Enter Username"
                    placeholderTextColor="#A9A9A9"
                    autoCapitalize="none"
                    autoCorrect={false}
                    secureTextEntry={!showUsername}
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowUsername(!showUsername)}
                  >
                    <Image
                      source={showUsername ?
                        require('../../../../assets/icons/Eye.png') :
                        require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>

              <View style={styles.inputGroup}>
                <Text style={styles.label}>Portal Password 🔒</Text>
                <View style={styles.inputWithIcon}>
                  <TextInput
                    style={[styles.input, styles.inputWithEye]}
                    value={formData.password}
                    onChangeText={(text) => setFormData({ ...formData, password: text })}
                    placeholder="Enter Password"
                    placeholderTextColor="#A9A9A9"
                    secureTextEntry={!showPassword}
                    autoCapitalize="none"
                    autoCorrect={false}
                    textContentType="password"
                  />
                  <TouchableOpacity
                    style={styles.eyeIcon}
                    onPress={() => setShowPassword(!showPassword)}
                  >
                    <Image
                      source={showPassword ?
                        require('../../../../assets/icons/Eye.png') :
                        require('../../../../assets/icons/Eye-closed.png')}
                      style={styles.eyeIconImage}
                      resizeMode="contain"
                    />
                  </TouchableOpacity>
                </View>
                <Text style={styles.vaultText}>Stored securely - shared only after EOL</Text>
              </View>
            </>
          )}

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Street Address</Text>
            <TextInput
              style={styles.input}
              value={formData.street}
              onChangeText={(text) => setFormData({ ...formData, street: text })}
              placeholder="Enter Street Address"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>City</Text>
            <TextInput
              style={styles.input}
              value={formData.city}
              onChangeText={(text) => setFormData({ ...formData, city: text })}
              placeholder="Enter City"
              placeholderTextColor="#A9A9A9"
              autoCapitalize="words"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>State</Text>
            <TouchableOpacity
              style={[styles.input, { flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }]}
              onPress={() => {
                logger.debug('Opening state modal');
                setShowStateModal(true);
              }}
            >
              <Text style={[
                { fontSize: 16, color: '#1C2C4F' },
                !formData.state && { color: '#A9A9A9' }
              ]}>
                {formData.state || 'Select State'}
              </Text>
              <Image
                source={require('../../../../assets/icons/down.png')}
                style={[styles.dropdownIcon, { tintColor: '#1C2C4F' }]}
              />
            </TouchableOpacity>
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Zip Code</Text>
            <TextInput
              style={styles.input}
              value={formData.zip}
              onChangeText={(text) => setFormData({ ...formData, zip: text })}
              placeholder="Enter Zip Code"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={5}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Years as Patient</Text>
            <TextInput
              style={styles.input}
              value={formData.yearsAsPatient}
              onChangeText={(text) => setFormData({ ...formData, yearsAsPatient: text })}
              placeholder="Enter Years as Patient"
              placeholderTextColor="#A9A9A9"
              keyboardType="numeric"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Last Visit Date</Text>
            <TextInput
              style={styles.input}
              value={formData.lastVisitDate}
              onChangeText={(text) => setFormData({ ...formData, lastVisitDate: formatDate(text) })}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Next Appointment Date</Text>
            <TextInput
              style={styles.input}
              value={formData.nextAppointmentDate}
              onChangeText={(text) => setFormData({ ...formData, nextAppointmentDate: formatDate(text) })}
              placeholder="MM/DD/YYYY"
              placeholderTextColor="#A9A9A9"
              keyboardType="number-pad"
              maxLength={10}
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Medical Record Number</Text>
            <TextInput
              style={styles.input}
              value={formData.medicalRecordNumber}
              onChangeText={(text) => setFormData({ ...formData, medicalRecordNumber: text })}
              placeholder="Enter Medical Record Number"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Insurance Accepted</Text>
            <TextInput
              style={styles.input}
              value={formData.insuranceAccepted}
              onChangeText={(text) => setFormData({ ...formData, insuranceAccepted: text })}
              placeholder="Enter Insurance Information"
              placeholderTextColor="#A9A9A9"
            />
          </View>

          <View style={styles.inputGroup}>
            <Text style={styles.label}>Special Instructions</Text>
            <TextInput
              style={[styles.input, styles.textArea]}
              value={formData.specialInstructions}
              onChangeText={(text) => setFormData({ ...formData, specialInstructions: text })}
              placeholder="Enter any Special Instructions"
              placeholderTextColor="#A9A9A9"
              multiline
              numberOfLines={4}
            />
          </View>

          <View style={styles.buttonContainer}>
            <TouchableOpacity
              style={[styles.submitButton, submitting && { opacity: 0.7 }]}
              onPress={handleSubmit}
              disabled={submitting}
            >
              {submitting ? (
                <ActivityIndicator color="#FFFFFF" />
              ) : (
                <Text style={styles.submitButtonText}>{isEditing ? 'Update' : 'Submit'}</Text>
              )}
            </TouchableOpacity>
          </View>
          <View style={styles.bottomPadding} />
        </ScrollView>
      </View>

      <Modal
        visible={showStateModal}
        transparent
        animationType="slide"
        onRequestClose={() => setShowStateModal(false)}
        statusBarTranslucent
      >
        <TouchableOpacity
          style={styles.modalOverlay}
          activeOpacity={1}
          onPress={() => setShowStateModal(false)}
        >
          <View style={styles.modalContent}>
            <View style={styles.modalHeader}>
              <Text style={styles.modalTitle}>Select State</Text>
              <TouchableOpacity
                style={styles.modalCloseButton}
                onPress={() => setShowStateModal(false)}
              >
                <Text style={styles.modalCloseText}>✕</Text>
              </TouchableOpacity>
            </View>
            <ScrollView style={styles.stateList}>
              {US_STATES.map((state) => (
                <TouchableOpacity
                  key={state}
                  style={styles.stateItem}
                  onPress={() => handleStateSelect(state)}
                >
                  <Text style={styles.stateItemText}>{state}</Text>
                </TouchableOpacity>
              ))}
            </ScrollView>
          </View>
        </TouchableOpacity>
      </Modal>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: ms(15),
    paddingTop: ms(25),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    padding: ms(15),
    paddingTop: ms(50),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#fff',
  },
  topSection: {
    alignItems: 'center',
    paddingVertical: ms(20),
  },
  backgroundKey: {
    position: 'absolute',
    width: '85%',
    height: '85%',
    opacity: 0.1,
  },
  logo: {
    width: s(120),
    height: s(120),
    marginBottom: ms(16),
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: 'bold',
    color: '#FFFFFF',
    marginBottom: ms(8),
  },
  inputContainer: {
    flex: 1,
    backgroundColor: '#F3F6F8',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    paddingTop: ms(20),
  },
  scrollView: {
    paddingHorizontal: ms(16),
  },
  scrollViewContent: {
    paddingBottom: ms(350),
  },
  inputGroup: {
    marginBottom: ms(16),
  },
  label: {
    fontSize: ms(16, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  vaultText: {
    fontSize: ms(12, 0.3),
    color: '#FF9F5A',
    marginTop: ms(5),
    fontStyle: 'italic',
    marginLeft: ms(2),
  },
  input: {
    backgroundColor: '#FFFFFF',
    borderRadius: ms(25),
    padding: ms(15),
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    borderWidth: ms(1),
    borderColor: '#d8dbdd',
  },
  inputWithIcon: {
    position: 'relative',
  },
  inputWithEye: {
    paddingRight: ms(50),
  },
  eyeIcon: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -10 }],
    padding: ms(5),
    zIndex: 1,
  },
  eyeIconImage: {
    width: s(20),
    height: s(20),
    tintColor: '#6B7280',
  },
  textArea: {
    height: s(100),
    textAlignVertical: 'top',
  },
  buttonContainer: {
    paddingVertical: ms(20),
    paddingHorizontal: ms(16),
  },
  submitButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  submitButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  bottomPadding: {
    height: s(120),
  },
  dropdownIcon: {
    width: s(20),
    height: s(20),
  },
  modalOverlay: {
    flex: 1,
    backgroundColor: 'rgba(0, 0, 0, 0.5)',
    justifyContent: 'flex-end',
  },
  modalContent: {
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(20),
    borderTopRightRadius: ms(20),
    maxHeight: '80%',
    width: '100%',
    paddingBottom: ms(20),
  },
  modalHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: ms(16),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  modalTitle: {
    fontSize: ms(18, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
  },
  modalCloseButton: {
    padding: ms(8),
  },
  modalCloseText: {
    fontSize: ms(20, 0.3),
    color: '#1C2C4F',
  },
  stateList: {
    padding: ms(16),
  },
  stateItem: {
    paddingVertical: ms(12),
    borderBottomWidth: ms(1),
    borderBottomColor: '#d8dbdd',
  },
  stateItemText: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
  },
  toggleContainer: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingVertical: ms(8),
  },
  toggleLabel: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    flex: 1,
  },
  toggleWrapper: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: ms(12),
  },
  toggleButton: {
    width: s(60),
    height: s(32),
    backgroundColor: '#E5E7EB',
    borderRadius: ms(16),
    padding: ms(2),
    justifyContent: 'center',
  },
  toggleButtonActive: {
    backgroundColor: '#FB923C',
  },
  toggleCircle: {
    width: s(28),
    height: s(28),
    backgroundColor: '#FFFFFF',
    borderRadius: ms(14),
    alignSelf: 'flex-start',
    shadowColor: '#000',
    shadowOffset: {
      width: 0,
      height: 2,
    },
    shadowOpacity: 0.25,
    shadowRadius: 3.84,
    elevation: 5,
  },
  toggleCircleActive: {
    alignSelf: 'flex-end',
  },
  toggleText: {
    fontSize: ms(16, 0.3),
    fontWeight: '500',
    color: '#1C2C4F',
    minWidth: s(30),
  },
  securityNoteContainer: {
    flexDirection: 'row',
    backgroundColor: '#FFF3E0',
    borderRadius: ms(12),
    padding: ms(15),
    marginBottom: ms(16),
    marginTop: ms(8),
    borderLeftWidth: ms(4),
    borderLeftColor: '#FF9F5A',
  },
  infoIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#FF9F5A',
    marginRight: ms(12),
    marginTop: ms(2),
  },
  securityNoteText: {
    flex: 1,
    fontSize: ms(14, 0.3),
    color: '#5D4037',
    lineHeight: ms(20, 0.3),
  },
  securityNoteTitle: {
    fontWeight: '700',
    color: '#FF9F5A',
  },
});